import type { Metadata } from "@/lib/head";

import { formatDateTime } from "@buildpy/core/lib/format";
import { canStartChangeOrder, changeableLogFilter, newChangeOrderPath } from "@buildpy/core/modules/change-order/view";
import { logDateBounds } from "@buildpy/core/modules/project/build-log";
import { countsPhaseDelay, delayedPhaseCount } from "@buildpy/core/modules/project/phase-schedule";
import { photoPhasesOn } from "@buildpy/core/modules/project/photo-phase";
import { recordBlockReason, recordDeleteBlockReason } from "@buildpy/core/modules/project/transitions";
import type { ProjectLog, ProjectPhase } from "@buildpy/core/modules/project/types";
import LogComposeSheet from "@/modules/project/ui/log-compose-sheet";
import LogList from "@/modules/project/ui/log-list";
import PhaseCard from "@/modules/project/ui/phase-card";
import PhotoGrid from "@/modules/project/ui/photo-grid";
import PhotoUploadButton from "@/modules/project/ui/photo-upload-button";
import TodayHeader from "@/modules/project/ui/today-header";
import { dDayOf } from "@buildpy/core/modules/project/view";
import type { ProjectTodayView } from "@buildpy/core/views/g3b/project-today";

export const metadata: Metadata = { title: "오늘 기록" };

interface Props {
  data: ProjectTodayView;
}

const DAY_MS = 86_400_000;

// `YYYY-MM-DD` 를 UTC 자정으로 하루씩 옮긴다 — 서버 시간대와 무관하다.
const shiftDate = (date: string, days: number): string =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

// 휴대폰에서 고칠 공정 — 진행·보류 전부 + 다음에 시작할 대기 하나.
const phasesToControl = (phases: readonly ProjectPhase[]): ProjectPhase[] => {
  const ordered = [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  const nextPending = ordered.find((p) => p.status === "PENDING");
  return ordered.filter((p) => p.status === "ACTIVE" || p.status === "HOLD" || p === nextPending);
};

// 오늘 기록 — 현장 담당자가 휴대폰(375px)으로 사진·일지를 남기는 화면. ?date= 로 지난 날도 본다(날짜 고르기는 core 읽기가 한다).
const Page = ({ data }: Props) => {
  const { project, phases, date, today, logs, photos, members, phaseDefs, contractStatus, changeOrders, member, logComments } = data;
  const bounds = logDateBounds(project, today);

  const blockReason = recordBlockReason(project);
  const canRecord = blockReason === null;
  const canEditPhases = project.status === "IN_PROGRESS" && contractStatus === "SIGNED";
  const canChange = contractStatus ? canStartChangeOrder(contractStatus, project.status) : false;
  const changeable = changeableLogFilter(changeOrders);
  const changeHrefOf = (log: ProjectLog): string | null =>
    canChange && changeable(log) ? newChangeOrderPath(project.contract_id, log.id) : null;
  const memberName = new Map(members.map((m) => [m.id, m.name]));
  const codeName = new Map(phaseDefs.map((p) => [p.code, p.name]));
  const phaseName = (phase: ProjectPhase): string => codeName.get(phase.phase_code) ?? phase.phase_code;
  const phaseById = new Map(phases.map((p) => [p.id, p]));
  const activePhases = phases.filter((p) => p.status === "ACTIVE");
  const activePhaseOptions = activePhases.map((p) => ({ id: p.id, name: phaseName(p) }));
  const photoPhases = photoPhasesOn(phases, date, today);
  const logPhaseOptions = [...phases].sort((a, b) => a.sort_order - b.sort_order).map((p) => ({ id: p.id, name: phaseName(p) }));
  const canDelete = (authorId: string | null): boolean =>
    canRecord && recordDeleteBlockReason(member, authorId) === null;
  const who = (authorId: string | null): string => (authorId && memberName.get(authorId)) || "알 수 없음";
  const timeOf = (iso: string): string => formatDateTime(iso).slice(-5);
  const logPhaseName = (id: string | null): string => {
    const phase = id ? phaseById.get(id) : undefined;
    return phase ? phaseName(phase) : "";
  };

  const prevDate = bounds.min !== null && date > bounds.min ? shiftDate(date, -1) : null;
  const nextDate = date < today ? shiftDate(date, 1) : null;
  const daysSinceStart = bounds.min === null ? null : -(dDayOf(bounds.min, date) ?? 0);

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-xl flex-col gap-y-4" data-testid="project-today">
      <TodayHeader
        activePhases={activePhaseOptions}
        date={date}
        daysSinceStart={daysSinceStart}
        delayCount={countsPhaseDelay(project.status) ? delayedPhaseCount(phases, today) : 0}
        isToday={date === today}
        nextDate={nextDate}
        prevDate={prevDate}
        projectId={project.id}
        status={project.status}
        title={project.title}
      />

      {canRecord ? (
        <section className="flex flex-col gap-y-2">
          <PhotoUploadButton
            key={date}
            defaultPhaseId={photoPhases.defaultId}
            logDate={date}
            phases={photoPhases.options.map((p) => ({ id: p.id, name: phaseName(p) }))}
            projectId={project.id}
          />
          {/* 사진 버튼(key=date)과 형제라 접두를 단다 — 같은 key 면 React 가 경고하고 둘을 헷갈린다. */}
          <LogComposeSheet
            key={`log-${date}`}
            defaultPhaseId={photoPhases.defaultId}
            logDate={date}
            phases={logPhaseOptions}
            photoNote={photos.length > 0 ? `${date === today ? "오늘" : "이날"} 찍은 ${photos.length}장` : null}
            projectId={project.id}
            projectStatus={project.status}
          />
        </section>
      ) : (
        <section
          className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground"
          data-testid="today-blocked">
          {blockReason}
        </section>
      )}

      <section className="flex flex-col gap-y-2">
        <h2 className="text-sm font-semibold">
          사진 <span className="font-normal tabular-nums text-muted-foreground">{photos.length}</span>
        </h2>
        <PhotoGrid
          photos={photos.map((photo) => {
            const phase = photo.project_phase_id ? phaseById.get(photo.project_phase_id) : undefined;
            return {
              id: photo.id,
              caption: photo.caption,
              meta: [phase ? phaseName(phase) : "", who(photo.uploaded_by), timeOf(photo.created_at)]
                .filter(Boolean)
                .join(" · "),
              canDelete: canDelete(photo.uploaded_by),
            };
          })}
          projectId={project.id}
        />
      </section>

      <section className="flex flex-col gap-y-2">
        <h2 className="text-sm font-semibold">
          현장일지 <span className="font-normal tabular-nums text-muted-foreground">{logs.length}</span>
        </h2>
        <LogList
          logs={logs.map((log) => ({
            id: log.id,
            kind: log.kind,
            body: log.body,
            meta: [
              logPhaseName(log.project_phase_id),
              log.crew_count ? `${log.crew_count}명` : "",
              who(log.author_id),
              timeOf(log.created_at),
            ]
              .filter(Boolean)
              .join(" · "),
            canDelete: canDelete(log.author_id),
            changeHref: changeHrefOf(log),
            weather: log.weather ?? null,
            comments: logComments.filter((c) => c.log_id === log.id),
          }))}
          projectId={project.id}
        />
      </section>

      {canEditPhases && (
        <section className="flex flex-col gap-y-2" data-testid="today-phases">
          <h2 className="text-sm font-semibold">공정</h2>
          <ul className="flex flex-col gap-y-2">
            {phasesToControl(phases).map((phase) => (
              <PhaseCard key={phase.id} editableProjectId={project.id} name={phaseName(phase)} phase={phase} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default Page;
